import type { FC } from 'react';
import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
// pages
import AuthCallback from '@/pages/AuthCallback';
import Agents from '@/pages/Agents';
import AKB from '@/pages/AKB';
import Profile from '@/pages/Profile';
import Integrations from '@/pages/Integrations';
import Conversations from '@/pages/Conversations';
import Personas from '@/pages/Personas';
import TalkingHead from '@/pages/DEV/TalkingHead';
import NewoChat from '@/pages/DEV/NewoChat';
import Workflow from '@/pages/Workflow';
import Attributes from '@/pages/Attributes';
import CustomerAttributes from '@/pages/Attributes/Customer';
import PersonasAttributes from '@/pages/Attributes/Personas';
import ProjectsAttributes from '@/pages/Attributes/Projects';
import Sessions from '@/pages/Sessions';
import ProjectsListRoute from '@/pages/Projects/ProjectsListRoute';
import ProjectDetailRoute from '@/pages/Project/ProjectDetailRoute';
import VersionControl from '@/pages/VersionControl';
import Library from '@/pages/Library';
// components
import PrivateRoute from '@/components/PrivateRoute';
import Layout from '@/components/Layout';
import TechnicalSupportRestricted from '@/components/TechnicalSupportRestricted';
import { PATHS } from '@/const';
import NotFound from '@/pages/NotFound';
import Widgets from '@/pages/Widgets';
import { useIsVibeBuilderEnabled } from '@/hooks/useIsVibeBuilderEnabled';
import { ProfileTab } from '@/pages/Profile/Profile.const';

const Designer = lazy(async () => import('@/pages/Designer'));
const VibeBuilder = lazy(async () => import('@/pages/VibeBuilder'));

interface AppRoutesProps {
    isAuthorized: boolean;
}

const AppRoutes: FC<AppRoutesProps> = ({ isAuthorized }) => {
    const isVibeBuilderEnabled = useIsVibeBuilderEnabled();

    return (
        <Routes>
            <Route element={<PrivateRoute isAllowed={isAuthorized} />}>
                <Route element={<Layout />}>
                    <Route
                        element={<Navigate to={PATHS.projects} />}
                        path={PATHS.index}
                    />
                    <Route element={<Agents />} path={PATHS.agents} />
                    <Route element={<Designer />} path={PATHS.agent.builder} />
                    <Route
                        element={<ProjectsListRoute />}
                        path={PATHS.projects}
                    />
                    <Route element={<Widgets />} path={PATHS.widgets} />
                    <Route
                        element={<ProjectDetailRoute />}
                        path={`${PATHS.projects}/:projectId/:tab?`}
                    />
                    <Route
                        element={<Library />}
                        path={`${PATHS.projects}/:projectId/libraries/:libraryId/builder`}
                    />
                    <Route
                        element={<VersionControl />}
                        path={PATHS.versionControl}
                    />
                    <Route
                        element={<AKB />}
                        path={`${PATHS.akb}/:personaId?`}
                    />
                    <Route
                        element={<Integrations />}
                        path={PATHS.integrations}
                    />
                    <Route element={<TechnicalSupportRestricted />}>
                        <Route
                            element={<Conversations />}
                            path={PATHS.conversations}
                        />
                    </Route>
                    <Route element={<TechnicalSupportRestricted />}>
                        <Route element={<Personas />} path={PATHS.personas} />
                    </Route>
                    <Route element={<Workflow />} path={PATHS.workflow} />
                    <Route
                        element={<Attributes />}
                        path={PATHS.attributes.root}
                    >
                        <Route
                            element={<CustomerAttributes />}
                            path={PATHS.attributes.customer}
                        />
                        <Route
                            element={<PersonasAttributes />}
                            path={PATHS.attributes.personas}
                        />
                        <Route
                            element={<ProjectsAttributes />}
                            path={PATHS.attributes.projects}
                        />
                    </Route>
                    <Route element={<TechnicalSupportRestricted />}>
                        <Route element={<Sessions />} path={PATHS.sessions} />
                    </Route>
                    <Route
                        element={<Profile activeTab={ProfileTab.Account} />}
                        path={PATHS.profile.root}
                    />
                    <Route
                        element={<Profile activeTab={ProfileTab.Account} />}
                        path={PATHS.profile.account}
                    />
                    <Route
                        element={<Profile activeTab={ProfileTab.Members} />}
                        path={PATHS.profile.members}
                    />
                    <Route
                        element={<Profile activeTab={ProfileTab.Keys} />}
                        path={PATHS.profile.keys}
                    />
                    <Route
                        element={<Profile activeTab={ProfileTab.Advanced} />}
                        path={PATHS.profile.advanced}
                    />
                    <Route element={<TalkingHead />} path={PATHS.talkingHead} />
                    <Route element={<NewoChat />} path={PATHS.newoChat} />
                    {isVibeBuilderEnabled ? (
                        <Route
                            element={<VibeBuilder />}
                            path={PATHS.vibeBuilder}
                        />
                    ) : (
                        <Route
                            element={<Navigate replace to={PATHS.projects} />}
                            path={PATHS.vibeBuilder}
                        />
                    )}
                    <Route element={<NotFound />} path="*" />
                </Route>
            </Route>
            <Route
                element={<AuthCallback />}
                path={PATHS.auth.login.callback}
            />
        </Routes>
    );
};

export default AppRoutes;
